import { useMemo, useState } from 'react'
import { Avatar } from './Avatar'
import { Pill } from './Pill'
import { useApp } from '../store/AppContext'
import { COVER_REQUEST_STATUS_META, shiftDateLabel } from '../lib/cover'
import type { CoverCandidate } from '../lib/cover'
import type { ScheduleEvent } from '../types'

/**
 * "Find cover" — the participant / nominee picks replacements from their own
 * team and asks them. Nobody is assigned here: availability only means
 * "I may be free", so the shift stays open until a worker accepts.
 */
export function CoverPanel({ event, onDone }: { event: ScheduleEvent; onDone: () => void }) {
  const { allUsers, coverCandidatesForEvent, coverRequestsForEvent, sendCoverRequests } = useApp()
  const [selected, setSelected] = useState<string[]>([])
  const [note, setNote] = useState('')

  const candidates = coverCandidatesForEvent(event.id)
  const requests = coverRequestsForEvent(event.id)
  const withdrawnBy = allUsers.find((u) => u.id === event.cover?.originalAssigneeUserId)

  const { available, others } = useMemo(
    () => ({
      available: candidates.filter((c) => c.available),
      others: candidates.filter((c) => !c.available),
    }),
    [candidates],
  )

  const toggle = (userId: string) =>
    setSelected((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))

  const send = () => {
    if (selected.length === 0) return
    sendCoverRequests(event.id, selected, note)
    setSelected([])
    setNote('')
    onDone()
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3.5">
        <p className="text-sm font-bold text-rose-800">{event.title}</p>
        <p className="text-xs font-semibold text-rose-700">
          {shiftDateLabel(event.date)} · {event.startTime} – {event.endTime}
        </p>
        {withdrawnBy && (
          <p className="mt-1 text-xs text-rose-700">
            {withdrawnBy.name} can no longer do this shift
            {event.cover?.reason ? ` — “${event.cover.reason}”` : ''}.
          </p>
        )}
      </div>

      {requests.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Already asked</h3>
          <div className="flex flex-col gap-1.5">
            {requests.map((request) => {
              const person = allUsers.find((u) => u.id === request.requestedUserId)
              if (!person) return null
              return (
                <div key={request.id} className="flex items-center gap-2">
                  <Avatar user={person} size={22} />
                  <span className="flex-1 text-xs font-medium text-slate-700">{person.name}</span>
                  <Pill className={COVER_REQUEST_STATUS_META[request.status].style}>
                    {COVER_REQUEST_STATUS_META[request.status].label}
                  </Pill>
                </div>
              )
            })}
          </div>
        </section>
      )}

      <section>
        <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">
          Available that day
          {available.length > 0 && <span className="ml-1 text-emerald-600">({available.length})</span>}
        </h3>
        <p className="mb-2 mt-0.5 text-[11px] text-slate-400">
          A green dot means this team member marked themselves available — it doesn't book them in.
        </p>
        {available.length === 0 ? (
          <p className="rounded-2xl bg-slate-50 p-3 text-center text-xs text-slate-500">
            Nobody on your team has marked themselves available for {shiftDateLabel(event.date)}. You can still ask
            anyone below.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {available.map((candidate) => (
              <CandidateRow
                key={candidate.user.id}
                candidate={candidate}
                checked={selected.includes(candidate.user.id)}
                onToggle={() => toggle(candidate.user.id)}
              />
            ))}
          </div>
        )}
      </section>

      {others.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
            Not marked available that day
          </h3>
          <div className="flex flex-col gap-2">
            {others.map((candidate) => (
              <CandidateRow
                key={candidate.user.id}
                candidate={candidate}
                checked={selected.includes(candidate.user.id)}
                onToggle={() => toggle(candidate.user.id)}
              />
            ))}
          </div>
        </section>
      )}

      <label className="block text-xs font-semibold text-slate-600">
        <span className="mb-1 block">Add a note (optional)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="e.g. Same routine as usual, keys are with Mark."
          className="input"
        />
      </label>

      <button
        onClick={send}
        disabled={selected.length === 0}
        className={`w-full rounded-2xl py-3 text-sm font-bold text-white shadow-card ${
          selected.length === 0 ? 'bg-slate-300' : 'bg-brand-600'
        }`}
      >
        {selected.length === 0
          ? 'Choose who to ask'
          : `Send cover request to ${selected.length} ${selected.length === 1 ? 'person' : 'people'}`}
      </button>
      <p className="-mt-2 text-center text-[11px] text-slate-400">
        They'll get this in Marnixia and can accept or decline. The first person to accept takes the shift.
      </p>
    </div>
  )
}

function CandidateRow({
  candidate,
  checked,
  onToggle,
}: {
  candidate: CoverCandidate
  checked: boolean
  onToggle: () => void
}) {
  const { user, available, conflict, request, withdrewFromShift } = candidate
  const alreadyAsked = request?.status === 'pending'
  const disabled = alreadyAsked

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left ${
        checked ? 'border-brand-500 bg-brand-50' : 'border-slate-200 bg-white'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <div className="relative shrink-0">
        <Avatar user={user} size={40} />
        {available && (
          <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">{user.name}</p>
        <p className="text-xs text-slate-500">{user.title ?? 'Support Worker'}</p>
        <div className="mt-1 flex flex-wrap gap-1">
          {available ? (
            <Pill className="bg-emerald-100 text-emerald-700">Available</Pill>
          ) : (
            <Pill className="bg-slate-100 text-slate-500">Not marked available</Pill>
          )}
          {conflict && (
            <Pill className="bg-amber-100 text-amber-700">
              Clashes with {conflict.title} {conflict.startTime}
            </Pill>
          )}
          {withdrewFromShift && <Pill className="bg-slate-100 text-slate-500">Stepped back from this shift</Pill>}
          {alreadyAsked && <Pill className="bg-amber-100 text-amber-700">Already asked</Pill>}
          {request?.status === 'declined' && <Pill className="bg-slate-100 text-slate-500">Declined earlier</Pill>}
        </div>
      </div>

      {!disabled && (
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
            checked ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 text-transparent'
          }`}
        >
          ✓
        </span>
      )}
    </button>
  )
}

